import { auth } from '../config/firebase';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut as firebaseSignOut,
  browserLocalPersistence,
  setPersistence,
  UserCredential
} from 'firebase/auth';
import { collection, query, where, getDocs, addDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AVAILABLE_APPS } from '../services/appAccess';

const emailsCollection = collection(db, 'allowedEmails');
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY = 'emailAllowanceCache';

// Load cache from localStorage
const loadEmailCache = () => {
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    try {
      return new Map(Object.entries(JSON.parse(cached)));
    } catch (e) {
      
      return new Map();
    }
  }
  return new Map();
};

// Save cache to localStorage
const saveEmailCache = (cache: Map<string, { allowed: boolean; timestamp: number }>) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(cache)));
  } catch (e) {
    
  }
};

const emailAllowanceCache = loadEmailCache();

export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    
    
    // Ensure persistence is set to local
    await setPersistence(auth, browserLocalPersistence);
    
    // Create provider
    const provider = new GoogleAuthProvider();
    
    // Add Google Sheets scope
    provider.addScope('https://www.googleapis.com/auth/spreadsheets');
    
    const result = await signInWithPopup(auth, provider);
    
    if (!result.user.email) {
      throw new Error('No email provided');
    }

    // Get the ID token
    const token = await result.user.getIdToken();
    // Store the token and email in localStorage
    localStorage.setItem('authToken', token);
    localStorage.setItem('userEmail', result.user.email);
    
    
    return result;
  } catch (error) {
    
    throw error;
  }
};

export const signOut = async () => {
  try {
    
    // Clear stored auth data
    localStorage.removeItem('redirectUrl');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    // Sign out from Firebase
    await firebaseSignOut(auth);
    
  } catch (error) {
    
    throw error;
  }
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

// Function to refresh the auth token
export const refreshAuthToken = async () => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken(true); // Force token refresh
      localStorage.setItem('authToken', token);
      return token;
    } catch (error) {
      
      throw error;
    }
  }
  return null;
};

// Set up token refresh
auth.onAuthStateChanged(async (user) => {
  if (user) {
    try {
      // Get fresh token
      const token = await user.getIdToken();
      localStorage.setItem('authToken', token);
      if (user.email) {
        localStorage.setItem('userEmail', user.email);
      }

      // Set up token refresh before expiry (every 30 minutes)
      setInterval(async () => {
        try {
          await refreshAuthToken();
        } catch (error) {
          
        }
      }, 30 * 60 * 1000); // 30 minutes
    } catch (error) {
      
    }
  } else {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
  }
});

export const isEmailAllowed = async (email: string): Promise<boolean> => {
  try {
    const normalizedEmail = email.toLowerCase();
    

    // Check cache first
    const cached = emailAllowanceCache.get(normalizedEmail);
    const now = Date.now();
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      
      return cached.allowed;
    }

    // If not in cache or expired, check Firestore
    const q = query(emailsCollection, where('email', '==', normalizedEmail));
    const querySnapshot = await getDocs(q);
    
    // If email is found in allowedEmails, it's allowed
    if (!querySnapshot.empty) {
      const userData = querySnapshot.docs[0].data();
      
      // Note: Admin status does not automatically grant app access
      // App access is managed separately through the admin panel
      // Cache the result and save
      emailAllowanceCache.set(normalizedEmail, { allowed: true, timestamp: now });
      saveEmailCache(emailAllowanceCache);
      return true;
    }

    // If not found, check if it's a jlsapps.com email
    const isJLSEmail = normalizedEmail.endsWith('@jlsapps.com');
    
    if (isJLSEmail) {
      
      try {
        // Add to allowedEmails with admin privileges
        const emailDoc = {
          email: normalizedEmail,
          displayName: '',
          isAdmin: true,
          addedAt: new Date(),
          isManuallyAdded: false
        };
        
        const docRef = await addDoc(emailsCollection, emailDoc);
        

        // Create app access document with no apps by default
        // App access should be granted through the admin panel
        const appAccessRef = doc(collection(db, 'userAppAccess'), normalizedEmail);
        const appAccessData = {
          allowedApps: []
        };
        
        await setDoc(appAccessRef, appAccessData);
        

        // Cache the result and save
        emailAllowanceCache.set(normalizedEmail, { allowed: true, timestamp: now });
        saveEmailCache(emailAllowanceCache);
        return true;
      } catch (error) {
        
        throw error;
      }
    }

    
    // Cache the negative result and save
    emailAllowanceCache.set(normalizedEmail, { allowed: false, timestamp: now });
    saveEmailCache(emailAllowanceCache);
    return false;
  } catch (error) {
    
    // Don't cache errors, let them be retried
    return false;
  }
};

export const getDisplayNameFromAllowedEmails = async (email: string): Promise<string | null> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const q = query(emailsCollection, where('email', '==', normalizedEmail));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userData = querySnapshot.docs[0].data();
      return userData.displayName || null;
    }
    
    return null;
  } catch (error) {
    
    return null;
  }
};

export const getDisplayNameForUser = async (email: string): Promise<string | null> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const q = query(emailsCollection, where('email', '==', normalizedEmail));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userData = querySnapshot.docs[0].data();
      return userData.displayName || null;
    }
    return null;
  } catch (error) {
    
    return null;
  }
};
